import CalendarIcon from '@uniiverse/library/assets/icons/calendar.svg'
import MarkerIcon from '@uniiverse/library/assets/icons/marker.svg'
import { Fragment, FunctionComponent, default as React } from 'react'
import { InjectedIntlProps, injectIntl } from 'react-intl'
import { AppContext, AppContextProps } from 'src/AppContext'
import { EditTextButton } from 'src/components/calendar/EditTextButton'
import { EventManagerButton } from 'src/components/calendar/EventManagerButton'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { Share } from 'src/components/share'
import { useSupportsWebp } from 'src/hooks/useSupportsWebp'
import { useWindowWidth } from 'src/hooks/useWindowWidth'
import { guessZone } from 'src/lib/display-date'
import { formattedDateFullTimeRange, formattedDateStartTime } from 'src/lib/formatter'
import { DATE_DISPLAY_OPTIONS } from './constants'

import locale from './locale'

const style = require('./Header.css')
const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN', 'DEVELOPER', 'SALES', 'SALES_ADMIN']

const Placeholder = ({ isMobile }) => (
  isMobile ?
    <div className={style.placeholderContainerMobile}>
      <LoadingPlaceholder modifierClass={style.heroPlaceholder}>
        <LoadingPlaceholder modifierClass={style.heroContentPlaceholder} />
      </LoadingPlaceholder>
      <LoadingPlaceholder modifierClass={style.contentPlaceholder}>
        <LoadingContent width="170px" height="14px" />
        <LoadingContent width="232px" height="18px" />
        <LoadingContent width="170px" height="14px" />
        <LoadingContent width="170px" height="14px" />
      </LoadingPlaceholder>
    </div>
    :
    <div className={style.placeholderContainer}>
      <LoadingPlaceholder modifierClass={style.placeholder}>
        <LoadingContent width="170px" lightBGColor />
        <LoadingContent width="300px" lightBGColor />
        <LoadingContent width="170px" lightBGColor />
        <LoadingContent width="170px" lightBGColor />
      </LoadingPlaceholder>
    </div>
)

interface Props {
  eventData: any, // TODO: Add graphql type
  loading: Boolean,
  isEventSet: Boolean,
  setTicketButtonText?: Function,
  ticketButtonText?: String,
}

const HeaderComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  loading,
  isEventSet,
  intl,
  setTicketButtonText,
  ticketButtonText,
}) => {
  const { isMobile } = useWindowWidth() // Note: React hooks cannot be called conditionally
  const supportsWebp = useSupportsWebp()

  if (!eventData || loading) return <Placeholder isMobile={isMobile} />

  let headerBackgroundImage = 'none'
  let heroBackgroundImage = 'none'

  if (supportsWebp !== null) {
    const { cropHeight, cropWidth, cropX, cropY } = eventData.coverPhoto
    let cropParams = cropHeight
      ? `resize/2400x/-/crop/${cropWidth * 2}x${cropHeight * 2}/${cropX * 2},${cropY * 2}`
      : 'scale_crop/2220x888/center'
    const baseUrl = `https://images.universe.com/${eventData.coverPhoto.uploadId}/-/progressive/yes/`
    const format = supportsWebp ? 'webp' : 'jpeg'
    headerBackgroundImage = `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${baseUrl}-/scale_crop/2220x888/center/-/blur/500/-/quality/lighter/-/format/${format}/)` // eslint-disable-line max-len

    if (isMobile) {
      cropParams = cropHeight
        ? `resize/1185x/-/crop/${cropWidth}x${cropHeight}/${cropX},${cropY}`
        : 'scale_crop/1110x444/center'
      heroBackgroundImage = `url(${baseUrl}-/${cropParams}/-/quality/lightest/-/format/${format}/)`
    } else {
      heroBackgroundImage = `linear-gradient(rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.4) 65%, rgba(0, 0, 0, 0.8) 100%), url('${baseUrl}-/${cropParams}/-/quality/lighter/-/format/${format}/')` // eslint-disable-line max-len
    }
  }

  const isUserAuthorized = (viewer) => {
    if (!viewer) return false

    const isAdmin = ADMIN_ROLES.indexOf(viewer.role) !== -1
    const isEventHost = viewer.id == eventData.user.id

    return isAdmin || isEventHost
  }

  const canDesignCurrentEvent = membership => membership.eventIds.includes(eventData.id) && membership.hasDesignPermission

  const canDesignEvent = (viewer) => {
    if (!viewer?.memberships?.nodes?.length) return false

    let canDesign = false
    viewer.memberships.nodes.forEach((membership) => {
      if (canDesignCurrentEvent(membership)) canDesign = true
    })

    return canDesign
  }

  const canViewReports = (viewer) =>
    (viewer?.memberships?.nodes || []).find((m)=> m.eventIds.includes(eventData.id) && m.hasAnyReportPermission)

  const timeslotText = () => {
    let startAt,
      endAt,
      tz = null

    if (isEventSet || eventData.timedEntry) {
      return intl.formatMessage(locale.multipleTimeslots)
    }

    if (eventData.virtual) {
      startAt = new Date(1000 * eventData.firstTimeSlot.startStamp)
      endAt = new Date(1000 * eventData.firstTimeSlot.endStamp)
      tz = guessZone(startAt)
    } else {
      startAt = eventData.firstTimeSlot.startAt
      endAt = eventData.firstTimeSlot.endAt
    }

    // display date and start time (no end time) e.g. Sat, Oct 12, 2025 at 6:00 PM
    if (eventData?.dateDisplayOption === DATE_DISPLAY_OPTIONS.startTimeOnly) {
      return formattedDateStartTime({
        startAt,
        intl,
        tz,
      })
    }

    return formattedDateFullTimeRange({
      startAt,
      endAt,
      intl,
      totalCount: eventData.totalTimeSlotCount,
      tz,
      showYear: true
    })
  }

  const location = eventData.virtual ? intl.formatMessage(locale.virtualEvent) : eventData.address

  return (
    <AppContext.Consumer>
      {({ viewer }: AppContextProps) => (
        <Fragment>
          <div className={style.header} style={{ backgroundImage: headerBackgroundImage }}>
            <div className={style.hero}>
              <div className={style.heroImage} style={{ backgroundImage: heroBackgroundImage }}>
                {viewer && !isEventSet && (
                  <div className={style.coverPhoto__button__container}>
                    <EventManagerButton
                      slugParam={eventData.slugParam}
                      eventId={eventData.id}
                      isAuthorized={isUserAuthorized(viewer) || canViewReports(viewer)}
                      memberships={viewer.memberships}
                    />
                    <EditTextButton
                      listing={eventData}
                      canDesign={isUserAuthorized(viewer) || canDesignEvent(viewer)}
                      setTicketButtonText={setTicketButtonText}
                      ticketButtonText={ticketButtonText}
                    />
                  </div>
                )}
              </div>
              <div className={style.content}>
                <h4 className={style.subheading}>{eventData.user.name}</h4>
                <h2 className={style.heading}>{eventData.title}</h2>
                <div>
                  {!eventData.hiddenDate && eventData.dateDisplayOption !== DATE_DISPLAY_OPTIONS.hideEventTime &&
                    <span className={style.location}>
                      <CalendarIcon className={style.icon} />
                      <p>{timeslotText()}</p>
                    </span>
                  }
                  <span className={style.location}>
                    <MarkerIcon className={style.icon} />
                    <p>{location}</p>
                  </span>
                </div>
                {isMobile && <Share />}
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </AppContext.Consumer>
  )
}

export const Header = injectIntl(HeaderComponent)
